import React, { useRef, useState } from 'react';
import type { LocationTag, MediaOverlays, Sport, User } from '../types';
import MediaEditor from './MediaEditor';
import OverlayLayer from './OverlayLayer';
import { hasOverlayContent } from '../utils/overlays';
import { SPORTS } from '../constants';
import XMarkIcon from './icons/XMarkIcon'; // Assuming XMarkIcon is in icons folder
import LightningIcon from './icons/LightningIcon'; // New lightning icon
import MapPinIcon from './icons/MapPinIcon';
import { readVideoMetadata } from '../utils/mediaUtils';

export interface UploadZipDraft {
  video: { file: File; previewUrl: string } | null;
  caption: string;
  tricks: string[];
  sport: Sport;
  locationTag: LocationTag | null;
  overlays?: MediaOverlays | null;
}

interface UploadZipPageProps {
  currentUser: User | null;
  onPromptLogin: () => void;
  onAddZip: (zipData: { videoFile: File; caption: string; sport: Sport; tricks?: string[]; locationTag?: LocationTag | null; overlays?: MediaOverlays | null }) => Promise<void>;
  onClose: () => void;
  initialDraft?: UploadZipDraft | null;
  onRequestLocationPick: (draft: UploadZipDraft) => void;
}

const UploadZipPage: React.FC<UploadZipPageProps> = ({ currentUser, onPromptLogin, onAddZip, onClose, initialDraft, onRequestLocationPick }) => {
  const [newZipVideo, setNewZipVideo] = useState<{ file: File; previewUrl: string } | null>(() => initialDraft?.video ?? null);
  const [newZipCaption, setNewZipCaption] = useState(() => initialDraft?.caption ?? '');
  const [newZipTricks, setNewZipTricks] = useState<string[]>(() => initialDraft?.tricks ?? []);
  const [newZipTrickInput, setNewZipTrickInput] = useState('');
  const [newZipSport, setNewZipSport] = useState<Sport>(() => initialDraft?.sport ?? SPORTS[0]);
  const [locationTag, setLocationTag] = useState<LocationTag | null>(() => initialDraft?.locationTag ?? null);
  const [overlays, setOverlays] = useState<MediaOverlays | null>(() => initialDraft?.overlays ?? null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleVideoSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      alert('Zips only support video uploads.');
      return;
    }
    try {
      const { duration } = await readVideoMetadata(file);
      if (duration > 180) {
        const message = 'Zips are limited to 3 minutes.';
        setUploadError(message);
        event.target.value = '';
        return;
      }
      setUploadError(null);
      setNewZipVideo({
        file,
        previewUrl: URL.createObjectURL(file),
      });
    } catch (err) {
      console.error('Failed to read zip video metadata', err);
      setUploadError('Could not read that video. Try a different file.');
      event.target.value = '';
    }
  };

  const handleCreateZip = async () => {
    if (!currentUser) {
      onPromptLogin();
      return;
    }
    if (!newZipVideo || isPosting) return;
    setIsPosting(true);
    setUploadError(null);
    try {
      await onAddZip({
        videoFile: newZipVideo.file,
        caption: newZipCaption,
        sport: newZipSport,
        tricks: newZipTricks,
        locationTag,
        overlays: hasOverlayContent(overlays) ? overlays : null,
      });
      onClose();
    } catch (err: any) {
      console.error('Failed to create zip', err);
      const message = err?.message || 'Upload failed. Please try again.';
      setUploadError(message);
      alert(message);
    } finally {
      setIsPosting(false);
    }
  };

  const addTrick = (rawTrick: string) => {
    const trick = rawTrick.trim();
    if (!trick) return;
    setNewZipTricks((prev) => {
      if (prev.some((existing) => existing.toLowerCase() === trick.toLowerCase())) {
        return prev;
      }
      return [...prev, trick].slice(0, 12);
    });
    setNewZipTrickInput('');
  };

  const removeTrick = (trickToRemove: string) => {
    setNewZipTricks((prev) => prev.filter((trick) => trick !== trickToRemove));
  };

  return (
    <div className="ridaz-fullpage fixed inset-0 z-[6000] overflow-y-auto bg-app-backdrop">
      <div className="mx-auto max-w-[820px] space-y-4 px-4 pb-6" style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 1.5rem)' }}>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-lime-500 text-black shadow-lg shadow-lime-500/25">
              <LightningIcon className="h-7 w-7" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Upload Zip</p>
              <p className="text-lg font-black text-white">Post a short-form clip</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-full border border-white/10 bg-white/5 p-2 hover:bg-white/15">
            <XMarkIcon className="h-6 w-6 text-white" />
          </button>
        </div>

        <div className="space-y-4 rounded-2xl border border-white/10 bg-app-surface p-4 shadow-2xl shadow-black/50">
          {!currentUser && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-100">
              Please sign in to upload a Zip.
            </div>
          )}

          <div className="rounded-2xl border border-lime-400/20 bg-lime-500/10 p-3 text-sm text-lime-100">
            Zips are video-only and capped at 3 minutes.
          </div>

          {uploadError && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-100">
              {uploadError}
            </div>
          )}

          <input type="file" ref={fileInputRef} onChange={handleVideoSelect} accept="video/*" className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="relative flex aspect-[9/16] w-full flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-black text-white transition-colors hover:border-lime-500"
          >
            {newZipVideo ? (
              <>
                <video src={newZipVideo.previewUrl} className="h-full w-full rounded-lg object-cover" controls playsInline />
                <OverlayLayer overlays={overlays} fit="cover" />
              </>
            ) : (
              <>
                <LightningIcon className="mb-3 h-12 w-12 text-lime-400" />
                <span className="font-medium">Select Video</span>
              </>
            )}
          </button>

          {newZipVideo && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className={`flex w-full items-center justify-center gap-2.5 rounded-2xl bg-lime-400 py-4 text-base font-black text-black shadow-lg ring-2 ring-white/30 transition active:scale-[0.98]`}
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19z" /><path d="M14.5 6.5l3 3" /></svg>
              {hasOverlayContent(overlays) ? 'Edited ✓  Tap to change' : 'Text & draw'}
            </button>
          )}

          <div>
            <label htmlFor="zip-caption" className="mb-1 block text-sm font-semibold text-gray-300">Caption</label>
            <textarea
              id="zip-caption"
              value={newZipCaption}
              onChange={(e) => setNewZipCaption(e.target.value)}
              rows={3}
              placeholder="Describe the clip..."
              className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-black focus:outline-none focus:ring-2 focus:ring-lime-500"
            />
          </div>

          <div>
            <label htmlFor="zip-tricks" className="mb-1 block text-sm font-semibold text-gray-300">Tricks</label>
            <input
              id="zip-tricks"
              type="text"
              value={newZipTrickInput}
              onChange={(e) => setNewZipTrickInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTrick(newZipTrickInput);
                }
              }}
              placeholder="Type a trick and press Enter"
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-black focus:outline-none focus:ring-2 focus:ring-lime-500"
            />
            {newZipTricks.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {newZipTricks.map((trick) => (
                  <button
                    key={trick}
                    type="button"
                    onClick={() => removeTrick(trick)}
                    className="rounded-full bg-lime-500 px-3 py-1 text-xs font-bold text-black transition hover:bg-lime-400"
                  >
                    {trick} ×
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-300">Location</label>
            {locationTag ? (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-lime-500/40 bg-lime-500/10 p-3">
                <div className="flex min-w-0 items-center gap-2">
                  <MapPinIcon className="h-5 w-5 shrink-0 text-lime-400" />
                  <span className="truncate text-sm font-bold text-lime-100">{locationTag.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setLocationTag(null)}
                  className="shrink-0 rounded-full p-1 text-lime-200 hover:bg-white/10"
                  aria-label="Remove location"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onRequestLocationPick({ video: newZipVideo, caption: newZipCaption, tricks: newZipTricks, sport: newZipSport, locationTag, overlays })}
                className="flex w-full items-center gap-2 rounded-xl border border-dashed border-white/20 bg-black px-3 py-3 text-sm font-semibold text-gray-300 transition-colors hover:border-lime-500 hover:text-lime-400"
              >
                <MapPinIcon className="h-5 w-5" />
                Add location
              </button>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-200">Select Sport</label>
            <div className="flex flex-wrap gap-2">
              {SPORTS.map((sport) => (
                <button
                  type="button"
                  key={sport.id}
                  onClick={() => setNewZipSport(sport)}
                  className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                    newZipSport.id === sport.id
                      ? 'bg-lime-500 font-bold text-black'
                      : 'border border-white/20 bg-black text-white hover:border-white/50'
                  }`}
                >
                  {sport.name}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleCreateZip}
            disabled={!newZipVideo || isPosting}
            className="w-full rounded-xl bg-lime-500 py-3 font-bold text-black transition-colors hover:bg-lime-400 disabled:cursor-not-allowed disabled:bg-gray-700 disabled:text-gray-500"
          >
            {isPosting ? 'Uploading Zip...' : 'Post Zip'}
          </button>
        </div>
      </div>
      {isEditing && newZipVideo && (
        <MediaEditor
          mediaUrl={newZipVideo.previewUrl}
          mediaType="video"
          initial={overlays}
          onCancel={() => setIsEditing(false)}
          onDone={(o) => { setOverlays(o); setIsEditing(false); }}
        />
      )}
    </div>
  );
};

export default UploadZipPage;
